/**
 * AdminExpenses — global queue + review for talent reimbursements.
 * Mounted at /admin/expenses. Allows filter by status; per-row Approve/Reject/Adjust.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import {
  Receipt, Loader2, FileText, CheckCircle2, XCircle, Clock, ArrowLeft, RefreshCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { formatDate } from '../../utils/timeFormat';
const STATUS_TABS = [
  { value: 'pending',  label: 'Pending'  },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const STATUS_BADGE = {
  pending:  { className: 'bg-amber-100 text-amber-800 border-amber-300',     icon: Clock },
  approved: { className: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
  rejected: { className: 'bg-red-100 text-red-800 border-red-300',           icon: XCircle },
};

export default function AdminExpenses() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null); // { row, action, adjusted_amount, notes }
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/expenses?status=${tab}&limit=200`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const submitReview = async () => {
    if (!review) return;
    if (review.action === 'reject' && !review.notes?.trim()) {
      toast.error('Notes required for reject');
      return;
    }
    if (review.action === 'adjust') {
      const a = parseFloat(review.adjusted_amount);
      if (!a || a <= 0) { toast.error('Enter a valid adjusted amount'); return; }
      if (!review.notes?.trim()) { toast.error('Notes required for adjust'); return; }
    }
    setSubmitting(true);
    try {
      const body = { action: review.action, review_notes: review.notes?.trim() || null };
      if (review.action === 'adjust') body.adjusted_amount = parseFloat(review.adjusted_amount);
      await api.patch(`/admin/expenses/${review.row.id}`, body);
      toast.success(`Expense ${review.action}d`);
      setReview(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const totalApproved = rows.filter((r) => r.status === 'approved')
    .reduce((s, r) => s + (r.effective_amount || 0), 0);

  return (
    <div className="container mx-auto p-4 max-w-6xl" data-testid="admin-expenses-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="mb-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="w-6 h-6 text-cyan-600" /> Reimbursements
          </h1>
          <p className="text-sm text-gray-500">Review pending expenses · approve, reject, or adjust amounts.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-expenses-btn">
          <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex gap-2">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setTab(t.value)}
                  className={`px-3 py-1.5 rounded text-sm font-medium ${
                    tab === t.value
                      ? 'bg-cyan-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`expenses-tab-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {tab === 'approved' && (
              <span className="text-sm text-gray-700">
                Total approved: <b>${totalApproved.toFixed(2)}</b>
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No {tab} expenses</div>
          ) : (
            <ul className="divide-y" data-testid="expenses-list">
              {rows.map((e) => {
                const cfg = STATUS_BADGE[e.status] || STATUS_BADGE.pending;
                const Icon = cfg.icon;
                return (
                  <li key={e.id} className="py-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`admin-expense-row-${e.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="font-semibold capitalize">{e.expense_type}</span>
                        <span className="font-bold text-gray-900">${e.effective_amount?.toFixed(2)}</span>
                        {e.adjusted_amount != null && e.adjusted_amount !== e.amount && (
                          <span className="text-[11px] text-gray-500 line-through">${e.amount?.toFixed(2)}</span>
                        )}
                        <Badge variant="outline" className={`text-[10px] ${cfg.className}`}>
                          <Icon className="w-3 h-3 mr-0.5 inline" />{e.status}
                        </Badge>
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        <b>{e.talent_name}</b> · {e.venue_name || 'Shift'}
                        {e.shift_date && ` · ${formatDate(e.shift_date)}`}
                        {e.client_company_name && ` · ${e.client_company_name}`}
                      </div>
                      {e.notes && <p className="text-[11px] text-gray-700 mt-0.5">{e.notes}</p>}
                      {e.review_notes && <p className="text-[11px] text-amber-700 mt-0.5"><b>Admin notes:</b> {e.review_notes}</p>}
                      {e.receipt_url && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/expenses/receipt/${e.receipt_url}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                          target="_blank" rel="noreferrer"
                          className="text-[11px] text-cyan-700 hover:underline inline-flex items-center gap-1 mt-0.5"
                        >
                          <FileText className="w-3 h-3" /> View receipt
                        </a>
                      )}
                    </div>
                    {e.status === 'pending' && (
                      <div className="flex gap-1.5 shrink-0">
                        <Button size="sm" onClick={() => setReview({ row: e, action: 'approve', adjusted_amount: '', notes: '' })} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`expense-approve-${e.id}`}>
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setReview({ row: e, action: 'adjust', adjusted_amount: e.amount, notes: '' })} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`expense-adjust-${e.id}`}>
                          Adjust
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setReview({ row: e, action: 'reject', adjusted_amount: '', notes: '' })} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`expense-reject-${e.id}`}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Review dialog */}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent data-testid="expense-review-dialog">
          <DialogHeader>
            <DialogTitle>
              {review?.action === 'approve' && 'Approve expense'}
              {review?.action === 'reject' && 'Reject expense'}
              {review?.action === 'adjust' && 'Adjust amount'}
            </DialogTitle>
            <DialogDescription>
              {review?.row?.talent_name} · {review?.row?.expense_type} · ${review?.row?.amount?.toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          {review && (
            <div className="space-y-3 py-2">
              {review.action === 'adjust' && (
                <div>
                  <Label className="text-sm">New approved amount</Label>
                  <Input
                    type="number" step="0.01"
                    value={review.adjusted_amount}
                    onChange={(e) => setReview({ ...review, adjusted_amount: e.target.value })}
                    data-testid="expense-adjust-input"
                  />
                </div>
              )}
              <div>
                <Label className="text-sm">
                  Notes {(review.action === 'reject' || review.action === 'adjust') && <span className="text-red-600">*</span>}
                </Label>
                <Textarea
                  rows={3}
                  value={review.notes}
                  onChange={(e) => setReview({ ...review, notes: e.target.value })}
                  placeholder={review.action === 'approve' ? 'Optional comment' : 'Required: explain why'}
                  data-testid="expense-review-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReview(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={submitReview} disabled={submitting} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="expense-review-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm ${review?.action || ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
